import { OkrMethodologyFacade } from "@gtmhub/goals/components/facades/okr-methodology.facade";
import { IRootScopeService, IPromise } from "angular";
import { IModalScope } from "angular-ui-bootstrap";
import { StateParams } from "@uirouter/angularjs";
import { IGoal } from "@gtmhub/goals/models";
import { AssigneeType, IAssignee } from "@gtmhub/assignees/models";
import { GoalService } from "@gtmhub/goals/goalService";
import { PermissionsService } from "@gtmhub/users/permissionsService";
import { IUIError, UIErrorHandlingService } from "@gtmhub/error-handling";
import { IMetric, MetricService, MetricLinkService, IMetricLinks, IMetricLinksSpec } from "@gtmhub/okrs/metrics";
import { INgRedux } from "@gtmhub/state-management";
import { ICustomField, CustomFieldActions, CfMap } from "@gtmhub/customFields";
import { ICustomFieldsStoreState } from "@gtmhub/customFields/redux/custom-field-reducer";
import { IStateInit } from "@gtmhub/core/routing";
import { AssigneeActions, IAssigneesStoreState } from "@gtmhub/assignees";
import { ITraceRootScopeService } from "@gtmhub/core/tracing";
import { goalIsNeitherObfuscatedNorLocked, isOkrAbandoned } from "@gtmhub/goals/utils";
import { IPlanningSession, ISessionsStoreState, PlanningSessionsActions } from "@gtmhub/sessions";
import { IdMap } from "@gtmhub/util";
import { IExtendedRootScopeService } from "@gtmhub/shared/ng-extensions";

interface IReduxBindings {
  customFields: ICustomField[];
  assignees: IdMap<IAssignee>;
  sessions: IdMap<IPlanningSession>;
  reduxItemsFetched: boolean;
  reduxItemsError: IUIError;
}

const bindStateToScope = (state: ICustomFieldsStoreState & IAssigneesStoreState & ISessionsStoreState): IReduxBindings => {
  return {
    customFields: state.customFields.map.metric,
    assignees: state.assignees.map,
    sessions: state.sessions.map,
    reduxItemsFetched: state.customFields.isFetched && state.assignees.isFetched && state.sessions.isFetched,
    reduxItemsError: state.customFields.error || state.assignees.error || state.sessions.error,
  };
};

export interface IAddSliceScope extends IModalScope, IReduxBindings {
  name: string;
  sliceSize: { value: number };
  selectedGoal: IGoal;
  metric: IMetric;
  goals: IGoal[];
  ownerIds: string[];
  selectedGoalUpdateAllowed: boolean;
  indicators: {
    loading?: boolean;
    saving?: boolean;
  };
  assigneeSelectorType: AssigneeType;
  multipleOwnersPerMetricDisabled: boolean;
  save(): void;
  selectGoal(goal: IGoal): void;
  selectMultipleOwners(ids: string[]): void;
  getContributingPercentage(): number;
}

export class AddSliceCtrl implements IStateInit {
  public static $inject = [
    "$rootScope",
    "$scope",
    "$stateParams",
    "GoalService",
    "MetricService",
    "MetricLinkService",
    "PermissionsService",
    "CustomFieldActions",
    "$ngRedux",
    "UIErrorHandlingService",
    "AssigneeActions",
    "PlanningSessionsActions",
    "OkrMethodologyFacade",
  ];

  constructor(
    private $rootScope: IRootScopeService & ITraceRootScopeService,
    private $scope: IAddSliceScope & IExtendedRootScopeService,
    private $stateParams: StateParams,
    private goalService: GoalService,
    private metricService: MetricService,
    private metricLinkService: MetricLinkService,
    private permissionsService: PermissionsService,
    private customFieldActions: CustomFieldActions,
    private $ngRedux: INgRedux,
    private uiErrorHandlingService: UIErrorHandlingService,
    private assigneeActions: AssigneeActions,
    private sessionsActions: PlanningSessionsActions,
    private okrMethodologyFacade: OkrMethodologyFacade
  ) {
    this.$scope.save = this.save;
    this.$scope.selectGoal = this.selectGoal;
    this.$scope.selectMultipleOwners = this.selectMultipleOwners;
    this.$scope.getContributingPercentage = this.getContributingPercentage;
  }

  public stateInit(): IPromise<unknown> {
    return this.init();
  }

  private init = (): IPromise<unknown> => {
    this.$scope.indicators = {
      loading: true,
      saving: false,
    };
    this.$scope.selectedGoalUpdateAllowed = true;
    this.$scope.sliceSize = { value: undefined };

    const unsubscribe = this.$ngRedux.connect(bindStateToScope)(this.$scope);
    this.$scope.$on("$destroy", unsubscribe);
    this.$ngRedux.dispatch(this.customFieldActions.getCustomFields());
    this.$ngRedux.dispatch(this.assigneeActions.getAssignees());
    this.$ngRedux.dispatch(this.sessionsActions.getSessionsIfMissing());

    return this.$scope
      .watchUntilAndRejectOnError(
        () => this.$scope.reduxItemsFetched,
        () => this.$scope.reduxItemsError
      )
      .then(() => {
        return this.metricService.getMetric(this.$stateParams.metricId).then((metric) => {
          this.$scope.metric = metric;
          this.$scope.name = metric.name;
          this.$scope.ownerIds = [...metric.ownerIds];

          return this.getChildrenGoals();
        });
      })
      .catch((error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true }));
  };

  private selectGoal = (goal: IGoal): IPromise<unknown> => {
    return this.$rootScope.traceAction("select_goal", () => {
      return this.permissionsService.getCurrentUserAllowedActionsPerItem(goal.id, "goal").then(
        (permissions) => {
          this.$scope.selectedGoalUpdateAllowed = permissions.currentUserActions.indexOf("update") !== -1;
          this.$scope.selectedGoal = goal;
          this.$scope.indicators.loading = false;
          this.setMetricMethodology();
        },
        (error) => this.uiErrorHandlingService.handleModal(error)
      );
    });
  };

  private setMetricMethodology(): void {
    const session = this.$scope.sessions[this.$scope.selectedGoal.sessionId];
    this.$scope.assigneeSelectorType = this.okrMethodologyFacade.getAssignMetricPermissions(session?.settings);
    this.$scope.multipleOwnersPerMetricDisabled = this.okrMethodologyFacade.areMultipleOwnersDisabled("metric", session?.settings);
  }

  private getChildrenGoals = (): IPromise<unknown> => {
    const attainmentTypeFilter = "average_kr,average_children_kr";

    return this.goalService.getGoals({ parentId: this.$scope.metric.goalId, attainmentTypes: attainmentTypeFilter }).then(
      (goals) => {
        this.$scope.goals = goals.filter((goal) => goalIsNeitherObfuscatedNorLocked(goal) && !isOkrAbandoned(goal.closedStatus));

        if (this.$scope.goals.length === 1) {
          return this.selectGoal(this.$scope.goals[0]);
        } else {
          this.$scope.indicators.loading = false;
        }
      },
      (error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "loading", inline: true })
    );
  };

  private save = () => {
    this.$rootScope.traceAction("save", () => {
      const slice = {
        manualType: this.$scope.metric.manualType,
        name: this.$scope.name,
        goalId: this.$scope.selectedGoal.id,
        target: this.$scope.sliceSize.value,
        targetOperator: this.$scope.metric.targetOperator,
        initialValue: 0,
        ownerIds: this.$scope.ownerIds,
        format: this.$scope.metric.format,
      };

      if (this.$scope.metric.customFields) {
        slice["customFields"] = this.getRequiredCustomFields();
      }

      this.$scope.indicators.saving = true;
      this.metricService
        .createMetric(slice)
        .then((childMetric) =>
          this.metricLinkService.link(slice.goalId, this.$scope.metric.id, "cascading", childMetric.id).then(() =>
            this.metricService.getMetric(childMetric.id).then((metric) => {
              this.$scope.indicators.saving = false;

              if (!this.$scope.metric.links) {
                this.$scope.metric.links = <IMetricLinks>{};
              }
              if (!this.$scope.metric.links.spec) {
                this.$scope.metric.links.spec = <IMetricLinksSpec>{};
              }
              if (!this.$scope.metric.links.spec.cascading) {
                this.$scope.metric.links.spec.cascading = [];
              }

              this.$scope.metric.links.spec.cascading.push({ id: metric.id, type: "metric" });

              this.$rootScope.$broadcast("sliceAdded", { cascading: this.$scope.metric, contributing: metric });
              this.$scope.$close();
            })
          )
        )
        .catch((error) => this.uiErrorHandlingService.handleIndicator(error, { indicator: "saving", inline: true }));
    });
  };

  private getContributingPercentage = (): number => {
    if (!this.$scope.sliceSize.value || !this.$scope.metric.target) {
      return 0;
    }
    return Math.round((this.$scope.sliceSize.value / this.$scope.metric.target) * 100);
  };

  private getRequiredCustomFields = (): CfMap => {
    Object.keys(this.$scope.metric.customFields).forEach((key) => {
      const field = this.$scope.customFields.find((field) => field.name === key);

      if (field && field.settings && field.settings.validations && !field.settings.validations.required) {
        delete this.$scope.metric.customFields[key];
      }
    });

    return this.$scope.metric.customFields;
  };

  private selectMultipleOwners = (ids: string[]): void => {
    if (!ids.length) {
      return;
    }

    this.$scope.ownerIds = ids;
  };
}
